import React, { useEffect, useState, useMemo } from "react";

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Switch,
} from "@mui/material";
import axios from "axios";
import { useDispatch } from "react-redux";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../../components/loader/Loader";
import { useForm } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormInputText } from "../../../components/form-components/FormInputText";
import SaveIcon from "@mui/icons-material/Save";
import { flavourSchema } from "../../../app/schema";
import GeneralImageUpload from "../../../components/form-components/GeneralImageUpload";
import { grey, orange } from "@mui/material/colors";
import { PhotoProvider, PhotoView } from "react-photo-view";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

import {
  showModal,
  storeFlavourList,
  showConfirmModal,
  hideConfirmModal,
} from "../../../redux/counterSlice";

import { debounce } from "lodash";
import {
  useTablePagination,
  useTableHeight,
} from "../../../hooks/useTablePagination";
import { usePermission } from "../../../hooks/usePermission";

function Flavour() {
  const dispatch = useDispatch();
  const permissions = usePermission("Flavour");
  // Use custom pagination/search hook
  const {
    page,
    rowsPerPage,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("flavour", 10);
  const tableHeight = useTableHeight();
  const hasActionPermission = permissions?.edit || permissions?.delete;

  // Local UI states
  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Controlled input for search box with debounce
  const [searchInput, setSearchInput] = useState(searchText);
  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        onSearchChange(val);
        // reset page to 0
        onChangePage(0);
      }, 500),
    [onSearchChange, onChangePage]
  );

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    debouncedSearch(val);
  };

  // React-hook-form setup with yup validation
  const methods = useForm({
    resolver: yupResolver(flavourSchema),
  });
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = methods;

  // Load flavour data whenever page, rowsPerPage or searchText changes
  useEffect(() => {
    setLoader(true);
    axios
      .get(`/getAllFlavourList`)
      .then((response) => {
        setFormData(response?.data?.data || []);
        setTotalCount(response?.data?.total || 0);
        reloadStore();
      })
      .catch((err) => {
        dispatch(
          showModal({
            msg: err?.message || "Failed to load flavour list",
            error: true,
          })
        );
      })
      .finally(() => setLoader(false));
  }, [page, rowsPerPage, searchText, dispatch]);

  // Reload active flavours list in redux store
  const reloadStore = () => {
    axios
      .get("/getAllActiveFlavourList")
      .then((response) => {
        if (response?.data?.data?.length > 0) {
          dispatch(storeFlavourList(response.data.data));
        }
      })
      .catch((err) => {
        dispatch(
          showModal({
            msg: err?.message || "Failed to reload active flavours",
            error: true,
          })
        );
      });
  };

  const handleClose = () => {
    setOpen(false);
    setOpenImage(false);
    setSelectedRow(null);
    reset();
  };

  const editClickHandler = (item) => {
    setSelectedRow({ ...item });
    setOpen(true);
    // populate form values for edit
    setValue("flavour_name", item.flavour_name);
  };

  const onSubmit = (data) => {
    saveForm(data);
  };

  // Toggle active status with confirmation modal
  const toggleStatus = (flavour_id) => {
    dispatch(
      showConfirmModal({
        msg: "Are you sure you want to change the status?",
        action: () => confirmToggleStatus(flavour_id),
      })
    );
  };

  const confirmToggleStatus = (flavour_id) => {
    setLoader(true);
    axios
      .get("/flavourSwitch/" + flavour_id)
      .then(() => {
        dispatch(
          showModal({ msg: "Status updated successfully", error: false })
        );
        reloadStore();
      })
      .catch((err) => {
        dispatch(
          showModal({
            msg: err?.message || "Failed to update status",
            error: true,
          })
        );
      })
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal());
      });
  };

  const saveForm = async (data) => {
    setLoader(true);
    try {
      let response;
      if (selectedRow?.flavour_id) {
        response = await axios.post("/updateFlavour", {
          ...data,
          flavour_id: selectedRow.flavour_id,
        });
      } else {
        response = await axios.post("/createFlavour", data);
      }

      if (!response?.data?.error) {
        dispatch(showModal({ msg: response.data.message, error: false }));
        reloadStore();
        setOpen(false);
        setSelectedRow(null);
        reset();
      } else {
        const errDesc = response?.data?.data
          ? Object.values(response.data.data).join("\n ")
          : response.data.message;
        dispatch(showModal({ msg: errDesc, error: true }));
      }
    } catch (error) {
      dispatch(
        showModal({ msg: error.message || "Something went wrong", error: true })
      );
    } finally {
      setLoader(false);
    }
  };

  // Dialog component for create/edit flavour
  const CreateEditDialog = React.memo(() => (
    <Dialog open={open} onClose={handleClose} maxWidth={"sm"}>
      <DialogTitle className="text-primary">
        {selectedRow ? "Edit Flavour" : "New Flavour"}
      </DialogTitle>
      <DialogContent>
        <Grid container columnSpacing={{ xs: 5 }}>
          <Grid item>
            <FormInputText
              name="flavour_name"
              control={control}
              label="Flavour Name"
              errors={errors}
              mandatory="true"
              defaultValue={selectedRow?.flavour_name}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="error">
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          startIcon={<SaveIcon />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  ));

  // Dialog for image update
  const UpdateImageDialog = () => (
    <Dialog open={openImage} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle className="text-primary">Update Image</DialogTitle>
      <Divider variant="middle" className="bg-primary" />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <GeneralImageUpload
              status={selectedRow?.image}
              name={selectedRow?.flavour_id?.toString()}
              control={control}
              label={selectedRow?.flavour_name}
              setValue={setValue}
              type="text"
              defaultValue={selectedRow?.image_url}
              loadInitialData={reloadStore}
              url="imageUpdateFlavour"
              formDataKey={{ id: "flavour_id", image: "flavour_image" }}
              dimension="Aspect Ratio 1:1"
              setOpenImage={setOpenImage}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="error">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  const columns = [
    {
      name: "flavour_id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => (
          <p>{dataIndex + 1 + page * rowsPerPage}</p>
        ),
      },
    },
    {
      name: "image",
      label: "Image",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const imageUrl = formData[dataIndex]?.image_url;
          return imageUrl ? (
            <PhotoProvider>
              <PhotoView key={dataIndex} src={imageUrl}>
                <img
                  src={imageUrl}
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                />
              </PhotoView>
            </PhotoProvider>
          ) : (
            <h3>-</h3>
          );
        },
      },
    },
    {
      name: "flavour_name",
      label: "Flavour Name",
    },
    {
      name: "product_count",
      label: "Product Count",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const count = formData[dataIndex]?.product_count || 0;
          return <Chip label={count} color="primary" />;
        },
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.active_status;
          const imageUrl = formData[dataIndex]?.image_url;

          return (
            <Switch
              disabled={!imageUrl}
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() => toggleStatus(formData[dataIndex]?.flavour_id)}
              style={{ marginBottom: 5 }}
            />
          );
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            options: {
              customBodyRenderLite: (dataIndex) => {
                const status = formData[dataIndex]?.active_status;

                return (
                  <div className="actionBtns">
                    <Button
                      disabled={status === 1}
                      aria-label="update-image"
                      onClick={() => {
                        setSelectedRow({ ...formData[dataIndex] });
                        setOpenImage(true);
                      }}
                    >
                      <AddPhotoAlternateIcon
                        sx={{ color: status === 1 ? grey[600] : orange[500] }}
                        fontSize="medium"
                      />
                    </Button>
                    {permissions?.edit && (
                      <Button
                        disabled={status === 1}
                        aria-label="edit"
                        onClick={() => editClickHandler(formData[dataIndex])}
                      >
                        <EditIcon
                          fontSize="medium"
                          sx={{
                            color: status === 1 ? grey[600] : "secondary.main",
                          }}
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    download: false,
    viewColumns: true,
    customSearch: false,
    print: false,
    page: page,
    rowsPerPage: rowsPerPage,
    count: totalCount,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    onTableChange: (action, tableState) => {
      switch (action) {
        case "changePage":
          onChangePage(tableState.page);
          break;
        case "changeRowsPerPage":
          onChangeRowsPerPage(tableState.rowsPerPage);
          break;
        default:
          break;
      }
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Flavour</h4>

          <MUIDataTable
            data={formData}
            columns={columns}
            options={options}
            title={
              permissions.add ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setSelectedRow(null);
                    reset();
                    setOpen(true);
                  }}
                  startIcon={<AddIcon />}
                >
                  New Flavour
                </Button>
              ) : null
            }
          />
        </>
      )}
      <CreateEditDialog />
      <UpdateImageDialog />
    </>
  );
}

export default Flavour;
